import { boxAtTime, type Track } from '../tracking/track';
import { boxCenter } from './box';
import { computeFrameLayout, type FrameLayout, type LayoutOptions, type Size } from './framing';

export interface FocusPoint {
  readonly t: number;
  readonly x: number;
  readonly y: number;
}

export interface ReframeOptions {
  /** Sampling step of the path in seconds. */
  readonly stepSec: number;
  /** Width of the zero-lag smoothing window; larger is calmer, smaller is snappier. */
  readonly smoothingSec: number;
  /** A new subject must be this much larger than the current one to take over. */
  readonly switchRatio: number;
  /** Faces the user kept visible are usually the creator; they weigh this much more. */
  readonly preferredWeight: number;
}

export const DEFAULT_REFRAME_OPTIONS: ReframeOptions = { stepSec: 0.1, smoothingSec: 1.2, switchRatio: 1.6, preferredWeight: 2.5 };

const HOLD = { holdSec: 0.5, maxInterpolationGapSec: 1.5 };

/** Centered moving average: smooth pans without the lag of a one-sided filter. */
function smooth(values: Float64Array, radius: number): Float64Array {
  if (radius <= 0) return values.slice();
  const prefix = new Float64Array(values.length + 1);
  for (let i = 0; i < values.length; i++) prefix[i + 1] = prefix[i]! + values[i]!;
  const out = new Float64Array(values.length);
  for (let i = 0; i < values.length; i++) {
    const from = Math.max(0, i - radius);
    const to = Math.min(values.length - 1, i + radius);
    out[i] = (prefix[to + 1]! - prefix[from]!) / (to - from + 1);
  }
  return out;
}

/**
 * Builds a camera path that follows the main subject: the largest face, with
 * user-revealed faces preferred, hysteresis against flip-flopping between
 * people, holding position while nobody is visible and zero-lag smoothing.
 */
export function computeFocusPath(
  faceTracks: readonly Track[],
  durationSec: number,
  fallback: { x: number; y: number },
  preferredIds: ReadonlySet<string> = new Set(),
  options: ReframeOptions = DEFAULT_REFRAME_OPTIONS,
): FocusPoint[] {
  const count = Math.max(1, Math.ceil(durationSec / options.stepSec) + 1);
  const xs = new Float64Array(count);
  const ys = new Float64Array(count);
  let subject: string | null = null;
  let target = fallback;
  let seenAny = false;
  let firstSeen = -1;

  for (let i = 0; i < count; i++) {
    const t = Math.min(durationSec, i * options.stepSec);
    let best: { id: string; weight: number; x: number; y: number } | null = null;
    let current: { weight: number; x: number; y: number } | null = null;
    for (const track of faceTracks) {
      const b = boxAtTime(track, t, HOLD);
      if (!b) continue;
      const center = boxCenter(b);
      const weight = b.w * b.h * (preferredIds.has(track.id) ? options.preferredWeight : 1);
      if (track.id === subject) current = { weight, ...center };
      if (!best || weight > best.weight) best = { id: track.id, weight, ...center };
    }
    if (best && (!current || best.weight > current.weight * options.switchRatio)) {
      subject = best.id;
      target = { x: best.x, y: best.y };
    } else if (current) {
      target = { x: current.x, y: current.y };
    } else {
      subject = null; // Nobody visible: hold the last position.
    }
    if (best && !seenAny) {
      seenAny = true;
      firstSeen = i;
    }
    xs[i] = target.x;
    ys[i] = target.y;
  }
  // Before the first face appears, start where the first face will be instead of panning in from the fallback.
  if (firstSeen > 0) {
    xs.fill(xs[firstSeen]!, 0, firstSeen);
    ys.fill(ys[firstSeen]!, 0, firstSeen);
  }
  const radius = Math.round(options.smoothingSec / options.stepSec / 2);
  const sx = smooth(xs, radius);
  const sy = smooth(ys, radius);
  return Array.from({ length: count }, (_, i) => ({ t: Math.min(durationSec, i * options.stepSec), x: sx[i]!, y: sy[i]! }));
}

/** Focus at an arbitrary time (linear interpolation between path samples). */
export function focusAt(path: readonly FocusPoint[], time: number): { x: number; y: number } | null {
  if (path.length === 0) return null;
  const step = path.length > 1 ? path[1]!.t - path[0]!.t : 1;
  const position = Math.min(path.length - 1, Math.max(0, (time - path[0]!.t) / step));
  const index = Math.floor(position);
  const a = path[index]!;
  const b = path[Math.min(path.length - 1, index + 1)]!;
  const k = position - index;
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
}

/** The frame layout at a given time: static framing, or following the focus path. */
export function layoutAtTime(display: Size, options: LayoutOptions, path: readonly FocusPoint[] | null, time: number): FrameLayout {
  const focus = path ? focusAt(path, time) : null;
  return computeFrameLayout(display, focus ? { ...options, focus } : options);
}
